// SPDX-FileCopyrightText: 2026 ParzLabs and Diffclaw contributors
// SPDX-License-Identifier: AGPL-3.0-only

import { type ReactNode, useEffect, useState } from "react";

import {
  ArrowRight,
  CircleAlert,
  Code2,
  History,
  ShieldCheck,
} from "lucide-react";

import type {
  ChronicleReadiness,
  CockpitApi,
  CockpitReviewListItem,
  HealthStatus,
  ProjectSummary,
} from "./api.js";
import {
  displayState,
  displayReviewRunState,
  pluralize,
  relativeTime,
  reviewReference,
  reviewTitle,
  stateTone,
} from "./presentation.js";

interface OverviewState {
  readonly health: HealthStatus | null;
  readonly project: ProjectSummary | null;
  readonly readiness: ChronicleReadiness | null;
  readonly reviews: readonly CockpitReviewListItem[];
}

function reviewIsOpen(review: CockpitReviewListItem): boolean {
  return !["cancelled", "completed", "failed", "superseded"].includes(
    review.runState,
  );
}

/*
 * One sentence naming the reviewer's next move. This replaced a display
 * headline that restated the queue count already visible in the metrics below.
 */
function leadSentence(
  loading: boolean,
  total: number,
  open: number,
  blindSpots: number,
): string {
  if (loading) return "Gathering the current review picture";
  if (total === 0) return "Nothing is waiting for review";
  if (open > 0) {
    return `${String(open)} ${pluralize(open, "change")} ${pluralize(open, "needs", "need")} analysis`;
  }
  if (blindSpots > 0) {
    return `Analysis is done, ${String(blindSpots)} ${pluralize(blindSpots, "blind spot")} left open`;
  }
  return `${String(total)} analyzed ${pluralize(total, "change")} ready for your decision`;
}

export function Overview({ api }: { readonly api: CockpitApi }) {
  const [state, setState] = useState<OverviewState>({
    health: null,
    project: null,
    readiness: null,
    reviews: [],
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void Promise.allSettled([
      api.health("ready"),
      api.project(),
      api.readiness(),
      api.reviews(),
    ]).then(([health, project, readiness, reviews]) => {
      if (!active) return;
      setState({
        health: health.status === "fulfilled" ? health.value : null,
        project: project.status === "fulfilled" ? project.value.project : null,
        readiness: readiness.status === "fulfilled" ? readiness.value : null,
        reviews: reviews.status === "fulfilled" ? reviews.value.reviews : [],
      });
      const failures = [health, project, readiness, reviews].filter(
        (result) => result.status === "rejected",
      );
      setError(
        failures.length === 0
          ? null
          : `${String(failures.length)} project ${pluralize(failures.length, "signal")} ${pluralize(failures.length, "is", "are")} temporarily unavailable`,
      );
    });
    return () => {
      active = false;
    };
  }, [api]);

  const openReviews = state.reviews.filter(reviewIsOpen);
  const blindSpots = state.reviews.reduce(
    (total, review) => total + review.blindSpotCount,
    0,
  );
  const loading = state.health === null && state.readiness === null;

  return (
    <div className="page">
      <header className="page-head">
        <div className="lead-line">
          <h1>
            {leadSentence(
              loading,
              state.reviews.length,
              openReviews.length,
              blindSpots,
            )}
          </h1>
        </div>
        <div className="page-head-actions">
          <a className="button primary" href="#/reviews">
            Open reviews
            <ArrowRight size={16} aria-hidden="true" />
          </a>
        </div>
      </header>

      {error === null ? null : (
        <div className="callout warning" role="status">
          <CircleAlert size={19} aria-hidden="true" />
          <div>
            <strong>{error}</strong>
            <p>
              What is shown below is the evidence that did load. Nothing has
              been filled in on its behalf.
            </p>
          </div>
        </div>
      )}

      <div className="metric-row">
        <div className="metric">
          <span className="metric-label">In the queue</span>
          <span className="metric-value">{state.reviews.length}</span>
        </div>
        <div className={openReviews.length > 0 ? "metric attention" : "metric"}>
          <span className="metric-label">Awaiting analysis</span>
          <span className="metric-value">{openReviews.length}</span>
        </div>
        <div className={blindSpots > 0 ? "metric attention" : "metric"}>
          <span className="metric-label">Blind spots</span>
          <span className="metric-value">{blindSpots}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Control plane</span>
          <span className="metric-value">
            <small>
              {state.health?.status === "ready" ? "Ready" : "Checking"}
            </small>
          </span>
        </div>
      </div>

      <div className="overview-grid">
        <section className="section">
          <div className="section-head">
            <h2>Needs your attention</h2>
            <a href="#/reviews">View all reviews</a>
          </div>
          {state.reviews.length === 0 ? (
            <div className="empty-state compact">
              <strong>No changes have reached the queue</strong>
              <span>
                Once a repository is connected, opened and updated pull requests
                appear here under their own titles.
              </span>
              <a className="button secondary" href="#/onboarding">
                Finish repository setup
              </a>
            </div>
          ) : (
            <div className="panel">
              {state.reviews.slice(0, 5).map((review) => {
                const reference = reviewReference(review.reviewSeriesId);
                return (
                  <a
                    className="attention-row"
                    href={`#/reviews/${encodeURIComponent(review.reviewSeriesId)}`}
                    key={review.reviewSeriesId}
                  >
                    <span className="attention-row-copy">
                      <strong>
                        {reviewTitle(review.declaredIntentSummary)}
                      </strong>
                      <small>
                        {reference === null
                          ? displayReviewRunState(review.runState)
                          : `${reference} · ${displayReviewRunState(review.runState)}`}
                      </small>
                    </span>
                    {review.blindSpotCount > 0 ? (
                      <span className="state-badge tone-attention">
                        {review.blindSpotCount}{" "}
                        {pluralize(review.blindSpotCount, "blind spot")}
                      </span>
                    ) : (
                      <span
                        className={`state-badge tone-${stateTone(review.runState)}`}
                      >
                        {displayReviewRunState(review.runState)}
                      </span>
                    )}
                    <time dateTime={review.updatedAt}>
                      {relativeTime(review.updatedAt)}
                    </time>
                    <ArrowRight size={16} aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          )}
        </section>

        <aside className="section">
          <div className="section-head">
            <h2>What reviews stand on</h2>
            <a href="#/onboarding">Manage</a>
          </div>
          <div className="panel">
            <CoverageItem
              detail={
                state.readiness === null
                  ? "Checking the repository state"
                  : `Canonical revision ${String(state.readiness.canonicalRevision)}`
              }
              icon={<Code2 size={17} aria-hidden="true" />}
              label="Current code"
              state={state.readiness?.currentCode ?? "pending"}
              ready={state.readiness?.currentCode === "current-code-ready"}
            />
            <CoverageItem
              detail={
                state.readiness?.unavailableSources.length
                  ? `${String(state.readiness.unavailableSources.length)} ${pluralize(state.readiness.unavailableSources.length, "source")} could not be read`
                  : "All accessible history retained"
              }
              icon={<History size={17} aria-hidden="true" />}
              label="Repository history"
              state={state.readiness?.historicalBackfill ?? "pending"}
              ready={state.readiness?.historicalBackfill === "complete"}
            />
            <CoverageItem
              detail="Versioned project knowledge used to plan reviews"
              icon={<ShieldCheck size={17} aria-hidden="true" />}
              label="Chronicle evidence"
              state={state.readiness?.projection ?? "pending"}
              ready={state.readiness?.projection === "current"}
            />
          </div>
          <p className="hint">
            Gaps stay visible here and inside each review. A thin history is
            reported, never quietly treated as a clean result.
          </p>
        </aside>
      </div>
    </div>
  );
}

function CoverageItem({
  detail,
  icon,
  label,
  ready,
  state,
}: {
  readonly detail: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly ready: boolean;
  readonly state: string;
}) {
  return (
    <div className="coverage-item">
      <span className={ready ? "coverage-icon ready" : "coverage-icon"}>
        {icon}
      </span>
      <span>
        <strong>{label}</strong>
        <small>{detail}</small>
      </span>
      <span
        className={`state-badge tone-${ready ? "neutral" : stateTone(state)}`}
      >
        {displayState(state)}
      </span>
    </div>
  );
}
